import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, Grid, TextField, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, FormHelperText } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { PlanData } from "types/models/planMaster/planMasterApp";
import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { updatePlan } from "redux/actions";
import planValidationSchema from "../planValidationSchema";

interface PlanProps {
  Plan?: PlanData;
}

const PlanUpdate: React.FC<PlanProps> = ({ Plan }) => {
  const navigate = useNavigate();
  const [fileError, setFileError] = useState(false);
  const dispatch = useDispatch();


  const initialValues = useMemo(
    () => ({
      planCategory: Plan?.planCategory || "Base",
      planClassification: Plan?.planClassification || "Special Plan",
      planDuration: Plan?.planDuration || "Monthly",
      validity: Plan?.validity || 1,
      planName: Plan?.planName || "",
      planCode: Plan?.planCode || "",
      speedType: Plan?.speedType || "Mbps",
      rentExclTax: Plan?.rentExclTax ?? 0,
      rentInclTax: Plan?.rentInclTax ?? 0,
      rentlTax: Plan?.rentlTax != null && Plan.rentlTax > 0 ? Plan.rentlTax : 1,
      primaryQuota: Plan?.primaryQuota ?? 0,
      secondaryQuota: Plan?.secondaryQuota ?? 0,
      popPlan: Plan?.popPlan || "N",
      popBestSeller: Plan?.popBestSeller || "Y",
      status: Plan?.status || "Active",
      popLableWifi: Plan?.popLableWifi || "",
      popPriceWifi: Plan?.popPriceWifi || 0,
      popDurationWifi: Plan?.popDurationWifi || "1",
      popOfferWifi: Plan?.popOfferWifi || "",
      popLableOtt: Plan?.popLableOtt || "",
      popPriceOtt: Plan?.popPriceOtt || 0,
      popDurationOtt: Plan?.popDurationOtt || "1",
      popOfferOtt: Plan?.popOfferOtt || "",
      planGroupCode: Plan?.planGroupCode,
      rentInclTaxWithOtt: Plan?.rentInclTaxWithOtt ?? null,
      rentExclTaxWithOtt: Plan?.rentExclTaxWithOtt ?? null,
      deviceSecurity:Plan?.deviceSecurity ?? '',
      basePriceOtt: Plan?.basePriceOtt || 0,
      basePriceWifi: Plan?.basePriceWifi || 0,
      popularHome: [
        Plan?.popPlanHomeWifi === 'Y' ? 'WI-FI' : null,
        Plan?.popPlanHomeOtt === 'Y' ? 'WI-FI + OTT' : null,
      ].filter(Boolean),
      popularPrice: [
        Plan?.popPlanPriceWifi === 'Y' ? 'WI-FI' : null,
        Plan?.popPlanPriceOtt === 'Y' ? 'WI-FI + OTT' : null,
      ].filter(Boolean),
    }),
    [Plan]
  );

  const handleSubmit = useCallback(
    async (values: any) => {
      const dataPayload = {
        planCategory: values.planCategory,
        planClassification: values.planClassification,
        planDuration: values.planDuration,
        validity: values.validity,
        planName: values.planName,
        planCode: values.planCode,
        speedType: values.speedType,
        rentExclTax: values.rentExclTax,
        rentInclTax: values.rentInclTax,
        rentlTax: values.rentlTax,
        primaryQuota: values.primaryQuota,
        secondaryQuota: values.secondaryQuota,
        popPlan: values.popPlan,
        popBestSeller: values.popBestSeller,
        status: values.status,
        popLableWifi: values.popLableWifi,
        popPriceWifi: values.popPriceWifi,
        popDurationWifi: values.popDurationWifi,
        popOfferWifi: values.popOfferWifi,
        planGroupCode: values.planGroupCode,
        rentInclTaxWithOtt: values.rentInclTaxWithOtt,
        rentExclTaxWithOtt: values.rentExclTaxWithOtt,
        basePriceOtt: values.basePriceOtt,
        basePriceWifi: values.basePriceWifi,
        popPlanHomeWifi: values.popularHome.includes('WI-FI') ? 'Y' : 'N',
        popPlanHomeOtt: values.popularHome.includes('WI-FI + OTT') ? 'Y' : 'N',
        popPlanPriceWifi: values.popularPrice.includes('WI-FI') ? 'Y' : 'N',
        popPlanPriceOtt: values.popularPrice.includes('WI-FI + OTT') ? 'Y' : 'N',

        popLableOtt: values.popLableOtt,
        popDurationOtt: values.popDurationOtt,
        popOfferOtt: values.popOfferOtt,
        popPriceOtt: values.popPriceOtt,
        deviceSecurity:values.deviceSecurity
      };

      try {
        const response = (await dispatch(
          updatePlan(Plan?.planId, dataPayload)
        )) as unknown as AxiosResponse;
        console.log("Response:", response);
        if (response?.status === 200) {
          navigate("/planMaster/ViewPlan/plan");
        }
      } catch (error) {
        console.error("Error during plan update:", error);
      }
    },
    [dispatch, navigate, Plan?.planId]
  );

  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          margin: 2,
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={planValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Category"
                    name="planCategory"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.planCategory && !!errors.planCategory}
                    helperText={<ErrorMessage name="planCategory" />}
                  >
                    <option value="Base">Base</option>
                    <option value="Limited">Limited</option>
                    <option value="Unlimited">Unlimited</option>
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Classification"
                    name="planClassification"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={
                      touched.planClassification && !!errors.planClassification
                    }
                    helperText={<ErrorMessage name="planClassification" />}
                  >
                    <option value="Special Plan">Special Plan</option>
                    <option value="Commercial">Commercial</option>
                  </Field>
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Name"
                    name="planName"
                    variant="outlined"
                    error={touched.planName && !!errors.planName}
                    helperText={<ErrorMessage name="planName" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Code"
                    name="planCode"
                    variant="outlined"
                    error={touched.planCode && !!errors.planCode}
                    helperText={<ErrorMessage name="planCode" />}
                    disabled
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Group Code"
                    name="planGroupCode"
                    variant="outlined"
                    error={touched.planGroupCode && !!errors.planGroupCode}
                    helperText={<ErrorMessage name="planGroupCode" />}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Speed Type"
                    name="speedType"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.speedType && !!errors.speedType}
                    helperText={<ErrorMessage name="speedType" />}
                  >
                    <option value="Mbps">Mbps</option>
                    <option value="Kbps">Kbps</option>
                  </Field>
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Duration"
                    name="planDuration"
                    variant="outlined"
                    error={touched.planDuration && !!errors.planDuration}
                    helperText={<ErrorMessage name="planDuration" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Validity"
                    name="validity"
                    variant="outlined"
                    error={touched.validity && !!errors.validity}
                    helperText={<ErrorMessage name="validity" />}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,2}$/.test(value)) {
                        setFieldValue("validity", value);
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Primary Quota"
                    name="primaryQuota"
                    variant="outlined"
                    error={touched.primaryQuota && !!errors.primaryQuota}
                    helperText={<ErrorMessage name="primaryQuota" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Secondary Quota "
                    name="secondaryQuota"
                    variant="outlined"
                    error={touched.secondaryQuota && !!errors.secondaryQuota}
                    helperText={<ErrorMessage name="secondaryQuota" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Device Security"
                    name="deviceSecurity"
                    variant="outlined"
                    error={touched.validity && !!errors.validity}
                    helperText={<ErrorMessage name="deviceSecurity" />}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Base Price - WiFi"
                    name="basePriceWifi"
                    variant="outlined"
                    error={touched.basePriceWifi && !!errors.basePriceWifi}
                    helperText={<ErrorMessage name="basePriceWifi" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Total Base Price - WiFi"
                    name="rentExclTax"
                    variant="outlined"
                    error={touched.rentExclTax && !!errors.rentExclTax}
                    helperText={<ErrorMessage name="rentExclTax" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Price Inc. Tax WiFi"
                    name="rentInclTax"
                    variant="outlined"
                    error={touched.rentInclTax && !!errors.rentInclTax}
                    helperText={<ErrorMessage name="rentInclTax" />}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Base Price - WiFi + OTT"
                    name="basePriceOtt"
                    variant="outlined"
                    error={
                      touched.basePriceOtt && !!errors.basePriceOtt
                    }
                    helperText={<ErrorMessage name="basePriceOtt" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Total Base Price - WiFi OTT"
                    name="rentExclTaxWithOtt"
                    variant="outlined"
                    error={touched.rentExclTaxWithOtt && !!errors.rentExclTaxWithOtt}
                    helperText={<ErrorMessage name="rentlTax" />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Price Inc. Tax WiFi + OTT"
                    name="rentInclTaxWithOtt"
                    variant="outlined"
                    error={touched.rentInclTaxWithOtt && !!errors.rentInclTaxWithOtt}
                    helperText={<ErrorMessage name="rentInclTaxWithOtt" />}
                  />
                </Grid>

              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Propular Plan"
                    name="popPlan"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.popPlan && !!errors.popPlan}
                    helperText={<ErrorMessage name="popPlan" />}
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </Field>
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Propular Best Seller"
                    name="popBestSeller"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.popBestSeller && !!errors.popBestSeller}
                    helperText={<ErrorMessage name="popBestSeller" />}
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </Field>
                </Grid>
              </Grid>
              {values.popPlan === "Y" && (
                <>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Label 1 WIFI"
                        name="popLableWifi"
                        variant="outlined"
                        error={touched.popLableWifi && !!errors.popLableWifi}
                        helperText={<ErrorMessage name="popLableWifi" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Label 2 WIFI"
                        name="popDurationWifi"
                        variant="outlined"
                        error={touched.popDurationWifi && !!errors.popDurationWifi}
                        helperText={<ErrorMessage name="popDurationWifi" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Price - WIFI"
                        name="popPriceWifi"
                        type="number"
                        variant="outlined"
                        error={touched.popPriceWifi && !!errors.popPriceWifi}
                        helperText={<ErrorMessage name="popPriceWifi" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Offer - WIFI"
                        name="popOfferWifi"
                        variant="outlined"
                        error={touched.popOfferWifi && !!errors.popOfferWifi}
                        helperText={<ErrorMessage name="popOfferWifi" />}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Label 1 WIFI +OTT "
                        name="popLableOtt"
                        variant="outlined"
                        error={touched.popLableOtt && !!errors.popLableOtt}
                        helperText={<ErrorMessage name="popLableOtt" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Label 2 WIFI +OTT"
                        name="popDurationOtt"
                        variant="outlined"
                        error={touched.popDurationOtt && !!errors.popDurationOtt}
                        helperText={<ErrorMessage name="popDurationOtt" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Price - WIFI +OTT"
                        name="popPriceOtt"
                        type="number"
                        variant="outlined"
                        error={touched.popPriceOtt && !!errors.popPriceOtt}
                        helperText={<ErrorMessage name="popPriceOtt" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Offer - WIFI +OTT"
                        name="popOfferOtt"
                        variant="outlined"
                        error={touched.popOfferOtt && !!errors.popOfferOtt}
                        helperText={<ErrorMessage name="popOfferOtt" />}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        error={touched.popularHome && Boolean(errors.popularHome)}
                      >
                        <InputLabel>Popular Plan - Home</InputLabel>
                        <Select
                          multiple
                          label="Popular Plan - Home"
                          name="popularHome"
                          value={values.popularHome}
                          onChange={(event) => setFieldValue('popularHome', event.target.value)}
                          renderValue={(selected) => (selected as unknown as string[]).join(', ')}
                        >
                          {['WI-FI', 'WI-FI + OTT'].map((plan) => (
                            <MenuItem key={plan} value={plan}>
                              <Checkbox checked={values.popularHome.includes(plan)} />
                              <ListItemText primary={plan} />
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {touched.popularHome && (errors.popularHome as string)}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        error={touched.popularPrice && Boolean(errors.popularPrice)}
                      >
                        <InputLabel>Popular Plan - Price Plan</InputLabel>
                        <Select
                          multiple
                          label="Popular Plan - Price Plan"
                          name="popularPrice"
                          value={values.popularPrice}
                          onChange={(event) => setFieldValue('popularPrice', event.target.value)}
                          renderValue={(selected) => (selected as unknown as string[]).join(', ')}
                        >
                          {['WI-FI', 'WI-FI + OTT'].map((plan) => (
                            <MenuItem key={plan} value={plan}>
                              <Checkbox checked={values.popularPrice.includes(plan)} />
                              <ListItemText primary={plan} />
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {touched.popularPrice && (errors.popularPrice as string)}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>

                </>
              )}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Status"
                    name="status"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.status && !!errors.status}
                    helperText={<ErrorMessage name="status" />}
                  >
                    <option value="New">New</option>
                    <option value="Active">Active</option>
                    <option value="InActive">Inactive</option>
                  </Field>
                </Grid>
              </Grid>

              <Box
                sx={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "right",
                  gap: 2,
                  paddingBottom: 2,
                  paddingTop: 2,
                  zIndex: 1000,
                  backgroundColor: "white",
                  boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  type="button"
                  onClick={() => navigate("/planMaster/ViewPlan/plan")}
                  sx={{ textTransform: "none", cursor: "pointer" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ textTransform: "none", cursor: "pointer", right: 4 }}
                >
                  {isSubmitting ? "Submitting..." : "Update Plan"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Grid>

      <Snackbar
        open={fileError}
        autoHideDuration={6000}
        onClose={() => setFileError(false)}
      >
        <Alert
          onClose={() => setFileError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {fileError
            ? "You can only upload one image."
            : "Plan Updated successfully!"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanUpdate;
